import React, { useState, useEffect, useRef } from "react";
import { ReligionTab } from "../types";
import { createAudioContext } from "../lib/audio-utils";
import { Bell, BellOff, Clock, Volume2, Sparkles, Check, AlertCircle } from "lucide-react";

interface DailyRitualReminderProps {
  activeReligion: ReligionTab;
}

const RELIGION_NAMES: Record<ReligionTab, string> = {
  hinduism: "Sanatana Dharma (Hinduism)",
  islam: "Islamic Sacred Salah & Dhikr",
  buddhism: "Buddhist Mindfulness & Chanting",
  christianity: "Christian Liturgy & Rosary",
  judaism: "Judaism Tefillah & Shabbat",
  sikhism: "Sikhi Gurbani & Simran",
  taoism: "Taoist Wu Wei Meditation",
  jainism: "Jain Navkar & Ahimsa",
  bahai: "Bahá'í Devotion & Unity",
  zoroastrianism: "Zoroastrian Asha & Gathas",
  shinto: "Shinto Misogi & Kami Gratitude",
  indigenous_americas: "Indigenous Four Directions Sacred Wheel",
  shamanism: "Shamanic Earth Drum Beats",
  universal: "Universal Interfaith Meditation"
};

export default function DailyRitualReminder({ activeReligion }: DailyRitualReminderProps) {
  const [reminderTime, setReminderTime] = useState<string>(() => {
    try {
      return localStorage.getItem("ritual_reminder_time") || "06:00";
    } catch (e) {
      return "06:00";
    }
  });

  const [isEnabled, setIsEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem("ritual_reminder_enabled") === "true";
    } catch (e) {
      return false;
    }
  });

  const [notificationPermission, setNotificationPermission] = useState<NotificationPermission>(() => {
    if (typeof window !== "undefined" && "Notification" in window) {
      return Notification.permission;
    }
    return "default";
  });

  const [lastTriggered, setLastTriggered] = useState<string | null>(null);
  const [showTestBanner, setShowTestBanner] = useState<boolean>(false);

  // Sound chime trigger
  const playReminderChime = () => {
    try {
      const ctx = createAudioContext();
      if (!ctx) return;

      // Play 3 harmonic bell tones
      const now = ctx.currentTime;
      [528, 660, 792].forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, now + idx * 0.25);

        gain.gain.setValueAtTime(0, now + idx * 0.25);
        gain.gain.linearRampToValueAtTime(0.3, now + idx * 0.25 + 0.05);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + idx * 0.25 + 1.8);

        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.start(now + idx * 0.25);
        osc.stop(now + idx * 0.25 + 1.8);
      });
    } catch (e) {
      console.warn("Chime error:", e);
    }
  };

  // Request Notification permission
  const requestNotificationPermission = async () => {
    if (typeof window !== "undefined" && "Notification" in window) {
      try {
        const result = await Notification.requestPermission();
        setNotificationPermission(result);
      } catch (e) {
        console.warn("Notification request error:", e);
      }
    }
  };

  // Trigger Notification & Audio
  const triggerReminder = () => {
    playReminderChime();
    setLastTriggered(new Date().toLocaleTimeString());

    if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
      try {
        new Notification("Sacred Ritual Reminder", {
          body: `It is time for your ${RELIGION_NAMES[activeReligion]} daily practice & meditation.`,
          icon: "/favicon.ico"
        });
      } catch (e) {
        // Fallback or caught safely
      }
    }
  };

  // Timer interval check every 20 seconds
  useEffect(() => {
    if (!isEnabled) return;

    const checkTime = () => {
      const now = new Date();
      const currentHours = String(now.getHours()).padStart(2, "0");
      const currentMinutes = String(now.getMinutes()).padStart(2, "0");
      const currentTimeStr = `${currentHours}:${currentMinutes}`;

      const todayKey = `${now.toDateString()}_${currentTimeStr}`;

      if (currentTimeStr === reminderTime && lastTriggered !== todayKey) {
        setLastTriggered(todayKey);
        triggerReminder();
      }
    };

    const interval = setInterval(checkTime, 20000);
    checkTime(); // check immediately

    return () => clearInterval(interval);
  }, [isEnabled, reminderTime, activeReligion, lastTriggered]);

  const handleToggle = () => {
    const next = !isEnabled;
    setIsEnabled(next);
    try {
      localStorage.setItem("ritual_reminder_enabled", String(next));
    } catch (e) {}

    if (next && notificationPermission === "default") {
      requestNotificationPermission();
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = e.target.value;
    setReminderTime(newTime);
    try {
      localStorage.setItem("ritual_reminder_time", newTime);
    } catch (e) {}
  };

  const handleTestChime = () => {
    triggerReminder();
    setShowTestBanner(true);
    setTimeout(() => setShowTestBanner(false), 4000);
  };

  return (
    <div className="rounded-2xl bg-gradient-to-br from-stone-900 via-stone-900/90 to-amber-950/30 border border-amber-500/20 p-5 shadow-lg backdrop-blur-md">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Title & Info */}
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl border transition-colors ${
            isEnabled 
              ? "bg-amber-500/20 border-amber-500/40 text-amber-300 shadow-md shadow-amber-500/10" 
              : "bg-stone-800 border-stone-700 text-stone-500"
          }`}>
            {isEnabled ? <Bell className="w-5 h-5 animate-pulse text-amber-400" /> : <BellOff className="w-5 h-5" />}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-stone-100 tracking-wide">Daily Ritual Chime & Notification</h3>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
                isEnabled ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300" : "bg-stone-800 border-stone-700 text-stone-400"
              }`}>
                {isEnabled ? "Active" : "Disabled"}
              </span>
            </div>
            <p className="text-xs text-stone-400">
              Set an exact time to receive an acoustic bell chime and web alert for {RELIGION_NAMES[activeReligion]}.
            </p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
          <div className="flex items-center gap-2 bg-stone-950 border border-stone-800 rounded-xl px-3 py-1.5">
            <Clock className="w-4 h-4 text-amber-400" />
            <input
              type="time"
              value={reminderTime}
              onChange={handleTimeChange}
              className="bg-transparent text-sm font-mono text-amber-200 font-bold focus:outline-none cursor-pointer"
            />
          </div>

          <button
            onClick={handleToggle}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all border ${
              isEnabled
                ? "bg-gradient-to-r from-amber-600 to-orange-600 text-white border-amber-400/50 shadow-md shadow-amber-900/30"
                : "bg-stone-800 hover:bg-stone-700 text-stone-300 border-stone-700"
            }`}
          >
            {isEnabled ? "Turn Off" : "Enable Reminder"}
          </button>
        </div>
      </div>

      {/* Permissions & Test Bar */}
      <div className="mt-4 pt-3 border-t border-stone-800/80 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 text-stone-400">
          {notificationPermission === "granted" ? (
            <span className="flex items-center gap-1 text-emerald-400 font-medium">
              <Check className="w-3.5 h-3.5" /> Desktop Alerts Permitted
            </span>
          ) : (
            <button
              onClick={requestNotificationPermission}
              className="flex items-center gap-1 text-amber-400 hover:underline"
            >
              <AlertCircle className="w-3.5 h-3.5" /> Request Browser Notification Permission
            </button>
          )}
        </div>

        <button
          onClick={handleTestChime}
          className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 hover:bg-amber-500/20 transition-colors"
        >
          <Volume2 className="w-3.5 h-3.5" /> Test Chime Sound
        </button>
      </div>

      {showTestBanner && (
        <div className="mt-3 p-2.5 rounded-xl bg-emerald-950/60 border border-emerald-500/30 text-emerald-200 text-xs flex items-center gap-2 animate-fadeIn">
          <Sparkles className="w-4 h-4 text-emerald-400" />
          <span>Acoustic chime & notification alert triggered successfully!</span>
        </div>
      )}
    </div>
  );
}
